import { AbstractControl, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { massHasNumber } from './mass-entry-contract';

export const MASS_UNITS = ['mg', 'g', 'kg'];

const MASS_MESSAGES: { [key: string]: string } = {
  required: 'این فیلد الزامی است.',
  asFoundRequired: 'قرائت‌های قبل از تنظیم (As Found) وارد نشده است.',
  nominalMagnitude: 'جرم نامی با مقادیر اندازه‌گیری سازگار نیست. مقدار یا واحد را بررسی کنید.',
  stageRequired: 'قرائت‌های مستقل بعد از تنظیم وارد نشده است.',
  unexpectedAsLeft: 'قرائت‌های بعد از تنظیم حفظ شده‌اند؛ وضعیت تنظیم را اصلاح یا چرخه‌ها را صریحاً حذف کنید.',
  uncertaintyRequired: 'عدم‌قطعیت استاندارد یا گسترده را از منبع معتبر وارد کنید؛ صفر واقعی قابل ثبت است.',
  rectangularRequired: 'نیم‌عرض بازه یا عدم‌قطعیت استاندارد را از منبع معتبر وارد کنید.',
  methodInvalid: 'روش ABA یا ABBA را انتخاب کنید.',
  contractVersion: 'نسخه قرارداد Mass پشتیبانی نمی‌شود.',
  legacyStageConflict: 'مرحله انتخاب‌شده قرائت دارد؛ قرائت‌های قبلی جایگزین نشدند.',
  legacyUnresolved: 'مرحله قرائت‌های قدیمی یا تعارض Cycles و As Found هنوز تعیین تکلیف نشده است؛ هیچ قرائتی حذف نشد.',
  relatedInputConflict: 'شناسه‌های ورودی مرجع یا حدود رکورد قبلی تکراری یا بدون اندازه‌گیری متناظر است. ذخیره برای جلوگیری از حذف اطلاعات متوقف شد؛ رکورد اصلی باید بازبینی شود.',
  responseModelMismatch: 'قرارداد پاسخ سرویس از نوع Mass نیست؛ ذخیره مجاز نیست.',
  loadFailed: 'دریافت رکورد جاری انجام نشد؛ ذخیره اطلاعات رکورد قبلی مجاز نیست.',
  positiveId: 'شناسه معتبر درخواست کالیبراسیون از سرویس دریافت نشده است.',
  repeatabilityRequired: 'برای یک چرخه، انحراف معیار معتبر روش لازم است.',
  referenceSelectionMissing: 'استاندارد مرجع در درخواست کالیبراسیون انتخاب نشده است.',
  referenceUncertainty: 'عدم‌قطعیت گواهی استاندارد مرجع مشخص نشده است.',
  coverageRequired: 'ضریب پوشش گواهی استاندارد باید مثبت باشد.',
  mpe: 'برای این جرم و کلاس حد مجاز تأییدشده موجود نیست.',
  limitsPending: 'حدود مجاز در حال دریافت از سرور است.',
  massUnit: 'واحد باید mg، g یا kg باشد.',
  finiteNumber: 'مقدار واردشده باید عددی باشد.',
  nonNegative: 'عدد باید صفر یا بزرگ‌تر باشد.',
  min: 'مقدار باید بزرگ‌تر از صفر باشد.',
  massCycles: 'تعداد قرائت‌ها با روش ABA/ABBA سازگار نیست.',
  uniqueMeasurementIds: 'شناسه‌های اندازه‌گیری باید مشخص و یکتا باشند.'
};
export function massValidationMessage(control: AbstractControl, key?: string): string {
  if (!control || !control.invalid || (!control.touched && !control.dirty)) { return ''; }
  if (key && !control.hasError(key)) { return ''; }
  const selected = key || ['server', 'asFoundRequired', 'nominalMagnitude', 'repeatabilityRequired', 'stageRequired', 'unexpectedAsLeft', 'legacyStageConflict', 'referenceSelectionMissing', 'referenceUncertainty', 'coverageRequired', 'uncertaintyRequired', 'rectangularRequired', 'methodInvalid', 'contractVersion', 'mpe', 'limitsPending', 'required', 'massUnit', 'finiteNumber', 'nonNegative', 'min', 'massCycles', 'uniqueMeasurementIds'].find(name => control.hasError(name));
  return selected === 'server' ? String(control.getError('server')) : MASS_MESSAGES[selected] || 'مقدار واردشده معتبر نیست.';
}

export function finiteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    massHasNumber(control.value)
      ? null : { finiteNumber: true };
}

export function nonNegativeFiniteNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = Number(control.value);
    return massHasNumber(control.value) && value >= 0 ? null : { nonNegative: true };
  };
}
export function optionalNonNegativeNumberValidator(): ValidatorFn {
  return control => control.value == null || control.value === '' ? null : nonNegativeFiniteNumberValidator()(control);
}

export function massUnitValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    MASS_UNITS.indexOf(control.value) >= 0 ? null : { massUnit: true };
}

export function massCycleValidator(methodControlName = 'Method', cyclesControlName = 'Cycles'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const method = Number(group.get(methodControlName) && group.get(methodControlName).value);
    const cycles = group.get(cyclesControlName) as FormArray;
    if ((method !== 0 && method !== 1) || !cycles || cycles.length === 0) { return { massCycles: true }; }
    const expectedTests = method === 0 ? 1 : 2;
    return cycles.controls.some(cycle => {
      const refs = cycle.get('ReferenceValues') as FormArray;
      const tests = cycle.get('TestValues') as FormArray;
      return !refs || refs.length !== 2 || !tests || tests.length !== expectedTests ||
        refs.controls.concat(tests.controls).some(item => !massHasNumber(item.value));
    }) ? { massCycles: true } : null;
  };
}

export function uniqueMeasurementIdsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const groups = control.get('Groups') as FormArray;
    if (!groups) { return null; }
    const ids: string[] = [];
    groups.controls.forEach(group => ((group.get('Measurements') as FormArray).controls || [])
      .forEach(item => ids.push(String(item.get('Id').value || '').trim().toLowerCase())));
    return ids.some((id, index) => !id || ids.indexOf(id) !== index) ? { uniqueMeasurementIds: true } : null;
  };
}
